import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { History, Loader2, Plus, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { PortalShell } from "@/components/PortalShell";
import { useAuth } from "@/lib/auth";
import { formatApiError } from "@/lib/api";

/** Backend validation details can be objects (e.g. overlapping_spend_entry). */
const errorText = (e) => {
  const detail = e?.response?.data?.detail;
  if (typeof detail === "string") return detail.replace(/_/g, " ");
  if (detail?.error) {
    return `${detail.error.replace(/_/g, " ")}${detail.existing_period
      ? ` (${detail.existing_period.join(" → ")})` : ""}`;
  }
  return formatApiError(e);
};
import {
  createSpend, deactivateSpend, fetchCampaigns, fetchReconciliation, fetchSpend,
  money, updateSpend,
} from "@/lib/marketing";

const input = "w-full rounded-lg border border-slate-300 px-3 py-2 text-sm";
const VERDICT_TONE = {
  reconciled: "bg-emerald-50 text-emerald-700 border-emerald-200",
  under_allocated: "bg-amber-50 text-amber-700 border-amber-200",
  over_allocated: "bg-rose-50 text-rose-700 border-rose-200",
  no_creative_spend_recorded: "bg-slate-100 text-slate-600 border-slate-200",
};

/** Marketing → Ad Spend. Manual campaign and creative spend records. */
export default function AdSpend() {
  const { user } = useAuth();
  const canEdit = user?.role === "admin" || user?.role === "marketing";
  const [campaigns, setCampaigns] = useState([]);
  const [rows, setRows] = useState(null);
  const [recon, setRecon] = useState(null);
  const [audit, setAudit] = useState(null);
  const [filters, setFilters] = useState({ campaign_id: "", level: "",
    include_deleted: false });
  const [form, setForm] = useState({ level: "campaign", granularity: "monthly",
    currency: "AUD" });

  const load = useCallback(async () => {
    const data = await fetchSpend(filters);
    setRows(data.spend);
    if (filters.campaign_id) {
      setRecon(await fetchReconciliation({ campaign_id: filters.campaign_id }));
    } else setRecon(null);
  }, [filters]);

  useEffect(() => {
    fetchCampaigns().then((d) => {
      setCampaigns(d.campaigns);
      const first = d.campaigns[0];
      setForm((f) => ({
        ...f,
        // The campaign DEFINITION this spend belongs to — spend reaches its
        // dashboard even before any utm-tagged traffic arrives.
        campaign_key: f.campaign_key || first?.campaign_id,
        campaign_id: f.campaign_id || first?.reporting_campaign_id
          || first?.campaign_id,
      }));
    }).catch(() => {});
  }, []);
  useEffect(() => { load().catch(() => toast.error("Unable to load ad spend")); }, [load]);

  const submit = async () => {
    try {
      await createSpend(form);
      toast.success("Ad spend recorded");
      load();
    } catch (e) {
      toast.error(errorText(e));
    }
  };

  return (
    <PortalShell title="Ad Spend" subtitle="Marketing · manual campaign and creative spend">
      <div className="flex flex-wrap gap-2 mb-5">
        <Link to="/admin/marketing/campaign-performance" data-testid="spend-link-performance"
          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold">
          View Performance
        </Link>
        <Link to="/admin/marketing/campaign-settings" data-testid="spend-link-campaign"
          className="rounded-full border border-slate-300 px-4 py-2 text-xs font-bold">
          View Campaign
        </Link>
      </div>

      {!canEdit && (
        <p data-testid="spend-read-only"
          className="rounded-xl bg-amber-50 border border-amber-200 p-3 text-xs font-bold
            text-amber-800 mb-4">
          Read-only access — ad spend can only be changed by an administrator.
        </p>
      )}

      <section className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4">
        <div className="grid grid-cols-1 sm:grid-cols-4 gap-3">
          <select data-testid="spend-filter-campaign" className={input}
            value={filters.campaign_id}
            onChange={(e) => setFilters((f) => ({ ...f, campaign_id: e.target.value }))}>
            <option value="">All campaigns</option>
            {[...new Set(campaigns.flatMap(
              (c) => [c.reporting_campaign_id, c.campaign_id].filter(Boolean)))]
              .map((c) => <option key={c} value={c}>{c}</option>)}
          </select>
          <select data-testid="spend-filter-level" className={input} value={filters.level}
            onChange={(e) => setFilters((f) => ({ ...f, level: e.target.value }))}>
            <option value="">All levels</option>
            <option value="campaign">Campaign level</option>
            <option value="creative">Creative level</option>
          </select>
          <label className="flex items-center gap-2 text-xs font-bold">
            <input type="checkbox" data-testid="spend-filter-deleted"
              checked={filters.include_deleted}
              onChange={(e) => setFilters((f) => ({ ...f, include_deleted: e.target.checked }))} />
            Include deactivated
          </label>
          {recon && (
            <span data-testid="spend-reconciliation-state"
              className={`self-center rounded-full border px-3 py-1 text-[11px] font-black
                ${VERDICT_TONE[recon.verdict] || "bg-slate-100 border-slate-200"}`}>
              {recon.verdict.replace(/_/g, " ").toUpperCase()} · difference{" "}
              {money(recon.difference)}
            </span>
          )}
        </div>
      </section>

      {canEdit && (
        <section data-testid="spend-add-form"
          className="rounded-2xl border border-slate-200 bg-white p-4 shadow-sm mb-4">
          <h2 className="text-sm font-black text-slate-900 mb-3">Add spend</h2>
          <div className="grid grid-cols-1 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            <input data-testid="spend-input-campaign" className={input} placeholder="campaign id"
              value={form.campaign_id || ""}
              onChange={(e) => setForm((f) => ({ ...f, campaign_id: e.target.value }))} />
            <select data-testid="spend-input-level" className={input} value={form.level}
              onChange={(e) => setForm((f) => ({ ...f, level: e.target.value }))}>
              <option value="campaign">Campaign level</option>
              <option value="creative">Creative level</option>
            </select>
            {form.level === "creative" && (
              <input data-testid="spend-input-creative" className={input}
                placeholder="utm_content" value={form.utm_content || ""}
                onChange={(e) => setForm((f) => ({ ...f, utm_content: e.target.value }))} />
            )}
            <select data-testid="spend-input-granularity" className={input}
              value={form.granularity}
              onChange={(e) => setForm((f) => ({ ...f, granularity: e.target.value }))}>
              <option value="daily">Daily</option>
              <option value="monthly">Monthly</option>
            </select>
            {form.granularity === "daily" ? (
              <input type="date" data-testid="spend-input-date" className={input}
                value={form.date || ""}
                onChange={(e) => setForm((f) => ({ ...f, date: e.target.value }))} />
            ) : (
              <input data-testid="spend-input-month" className={input} placeholder="YYYY-MM"
                value={form.month || ""}
                onChange={(e) => setForm((f) => ({ ...f, month: e.target.value }))} />
            )}
            <input type="number" step="0.01" data-testid="spend-input-amount" className={input}
              placeholder="amount" value={form.amount || ""}
              onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))} />
            <input data-testid="spend-input-currency" className={input} value={form.currency}
              onChange={(e) => setForm((f) => ({ ...f, currency: e.target.value }))} />
            <input data-testid="spend-input-note" className={input} placeholder="note"
              value={form.note || ""}
              onChange={(e) => setForm((f) => ({ ...f, note: e.target.value }))} />
            <button data-testid="spend-submit" onClick={submit}
              className="inline-flex items-center justify-center gap-1 rounded-full
                bg-slate-900 px-4 py-2 text-xs font-bold text-white">
              <Plus className="h-3 w-3" /> Add Spend
            </button>
          </div>
          <p className="text-[11px] text-slate-500 mt-2">
            Creative spend is a breakdown of campaign spend — the two are never added
            together. AUD only for ROAS; other currencies are stored but excluded.
          </p>
        </section>
      )}

      {!rows && (
        <p className="flex items-center gap-2 text-sm text-slate-500">
          <Loader2 className="h-4 w-4 animate-spin" /> Loading spend…
        </p>
      )}

      {rows && !rows.length && (
        <p data-testid="spend-empty" className="text-sm text-slate-500">
          No ad spend recorded yet — ROAS stays unavailable until spend is entered.
        </p>
      )}

      {rows && rows.length > 0 && (
        <div className="overflow-x-auto rounded-2xl border border-slate-200 bg-white">
          <table data-testid="spend-table" className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                {["Campaign", "Creative", "Level", "Granularity", "Period", "Amount",
                  "Currency", "Source", "Entered by", "Entered at", "Note", "State",
                  "Actions"].map((h) => (
                  <th key={h} className="px-3 py-2 whitespace-nowrap font-bold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {rows.map((r) => (
                <tr key={r.id} data-testid={`spend-row-${r.id}`}>
                  <td className="px-3 py-2">{r.campaign_id}</td>
                  <td className="px-3 py-2">{r.utm_content || "—"}</td>
                  <td className="px-3 py-2">{r.level}</td>
                  <td className="px-3 py-2">{r.granularity}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {r.period_start} → {r.period_end}
                  </td>
                  <td className="px-3 py-2 font-bold">{money(r.amount)}</td>
                  <td className="px-3 py-2">{r.currency}
                    {!r.roas_eligible && (
                      <span data-testid={`spend-non-aud-${r.id}`}
                        className="ml-1 text-[10px] font-black text-amber-700">
                        EXCLUDED FROM ROAS
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-2">{r.source}</td>
                  <td className="px-3 py-2">{r.entered_by}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    {(r.entered_at || "").slice(0, 16).replace("T", " ")}
                  </td>
                  <td className="px-3 py-2">{r.note || "—"}</td>
                  <td className="px-3 py-2">{r.status}</td>
                  <td className="px-3 py-2 whitespace-nowrap">
                    <button data-testid={`spend-audit-${r.id}`} onClick={() => setAudit(r)}
                      className="inline-flex items-center gap-1 text-xs font-bold text-slate-600">
                      <History className="h-3 w-3" /> Audit
                    </button>
                    {canEdit && r.status === "active" && (
                      <>
                        <button data-testid={`spend-edit-${r.id}`}
                          onClick={() => {
                            const amount = window.prompt("New amount", r.amount);
                            if (!amount) return;
                            updateSpend(r.id, { amount: Number(amount),
                              reason: "corrected in admin" })
                              .then(() => { toast.success("Spend updated"); load(); })
                              .catch((e) => toast.error(errorText(e)));
                          }}
                          className="ml-3 text-xs font-bold text-slate-600">Edit</button>
                        <button data-testid={`spend-delete-${r.id}`}
                          onClick={() => {
                            const reason = window.prompt("Reason for deactivating");
                            if (!reason) return;
                            deactivateSpend(r.id, reason)
                              .then(() => { toast.success("Spend deactivated"); load(); })
                              .catch((e) => toast.error(errorText(e)));
                          }}
                          className="ml-3 inline-flex items-center gap-1 text-xs font-bold
                            text-rose-600">
                          <Trash2 className="h-3 w-3" /> Deactivate
                        </button>
                      </>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {audit && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 flex items-center justify-center p-4"
          data-testid="spend-audit-overlay" onClick={() => setAudit(null)}>
          <div data-testid="spend-audit-modal" onClick={(e) => e.stopPropagation()}
            className="bg-white rounded-2xl p-5 max-w-lg w-full max-h-[80vh] overflow-y-auto">
            <h3 className="text-sm font-black text-slate-900 mb-2">
              Audit history · {audit.campaign_id} {audit.utm_content || ""}
            </h3>
            <ol className="space-y-2">
              {(audit.audit || []).map((a, i) => (
                <li key={i} className="text-xs text-slate-600 border-b border-slate-100 pb-2">
                  <b>{a.event}</b> · {a.at} · {a.by} ({a.by_role})
                  {a.before && <> · before {JSON.stringify(a.before)}</>}
                  {a.after && <> · after {JSON.stringify(a.after)}</>}
                  {a.reason && <> · {a.reason}</>}
                </li>
              ))}
            </ol>
            <button onClick={() => setAudit(null)}
              className="mt-3 rounded-full bg-slate-900 px-4 py-2 text-xs font-bold text-white">
              Close
            </button>
          </div>
        </div>
      )}
    </PortalShell>
  );
}
